import React, { useState, useEffect } from 'react';
import { X, User, Search } from 'lucide-react';
import { useProjectsStore, Task } from '../../../store/projectsStore';
import { useTeamStore } from '../../../store/teamStore';

interface TaskAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  task: Task;
}

const TaskAssignmentModal: React.FC<TaskAssignmentModalProps> = ({ isOpen, onClose, task }) => {
  const { updateTask } = useProjectsStore();
  const { teamMembers, fetchTeamMembers, isLoading } = useTeamStore();
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchTeamMembers();
    }
  }, [isOpen, fetchTeamMembers]);

  if (!isOpen) return null;

  // Get user IDs from team members - need to check the structure
  // Team members have userId field that needs to be extracted
  const filteredUsers = teamMembers
    .filter(member => {
      const searchLower = searchTerm.toLowerCase();
      return member.name.toLowerCase().includes(searchLower) ||
        member.email.toLowerCase().includes(searchLower);
    })
    .map(member => ({
      id: member.id, // This should be the userId
      name: member.name,
      email: member.email,
    }));

  const handleAssign = async (userId: string) => {
    await updateTask({
      id: task.id,
      assignedTo: userId,
    });
    onClose();
  };

  const handleUnassign = async () => {
    const updateData: any = { id: task.id };
    delete updateData.assignedTo;
    await updateTask(updateData);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Assign Task</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search users..."
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          <div className="space-y-2 max-h-64 overflow-y-auto">
            {task.assignedTo && (
              <button
                onClick={handleUnassign}
                className="w-full flex items-center gap-3 p-3 bg-red-50 border-2 border-red-200 rounded-lg hover:bg-red-100 transition-colors"
              >
                <User className="w-5 h-5 text-red-600" />
                <div className="flex-1 text-left">
                  <p className="font-medium text-red-900">Unassign</p>
                  <p className="text-sm text-red-600">Remove assignment</p>
                </div>
              </button>
            )}

            {filteredUsers.map(user => (
              <button
                key={user.id}
                onClick={() => handleAssign(user.id)}
                className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${
                  task.assignedTo === user.id
                    ? 'bg-primary-50 border-2 border-primary-500'
                    : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                }`}
              >
                <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold">
                  {user.name.charAt(0)}
                </div>
                <div className="flex-1 text-left">
                  <p className="font-medium text-gray-900">{user.name}</p>
                  <p className="text-sm text-gray-500">{user.email}</p>
                </div>
                {task.assignedTo === user.id && (
                  <div className="w-5 h-5 bg-primary-500 rounded-full flex items-center justify-center">
                    <X className="w-3 h-3 text-white" />
                  </div>
                )}
              </button>
            ))}

            {isLoading && (
              <p className="text-center text-gray-500 py-4">Loading team members...</p>
            )}
            {!isLoading && filteredUsers.length === 0 && (
              <p className="text-center text-gray-500 py-4">No users found</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskAssignmentModal;

